import React, { useEffect, useRef } from 'react';
import { useTipTapEditor, TipTapToolbar, EditorContent } from '../../utils/tipTapConfig';
import TagInput from './TagInput';
import './NoteEditor.css';

const NoteEditor = ({
  heading = '',
  summary = '',
  content = null,
  tags = [],
  onHeadingChange,
  onSummaryChange,
  onContentChange,
  onTagsChange,
  tagSuggestions = [],
  disabled = false
}) => {
  const summaryRef = useRef(null);

  // Initialize TipTap editor
  const editor = useTipTapEditor({
    content: content,
    placeholder: 'Start writing your note...',
    editable: !disabled,
    onUpdate: (json) => {
      if (onContentChange) {
        onContentChange(json);
      }
    }
  });

  // Handle Ctrl+A / Cmd+A for select all
  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a' && !event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        // Use TipTap's selectAll command
        editor.commands.selectAll();
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('keydown', handleKeyDown);

    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor]);

  // Update editor content when content prop changes
  useEffect(() => {
    if (editor) {
      const currentContent = editor.getJSON();
      const contentToSet = content || { type: 'doc', content: [] };
      // Only update if content actually changed (avoid infinite loops)
      if (JSON.stringify(currentContent) !== JSON.stringify(contentToSet)) {
        editor.commands.setContent(contentToSet);
      }
    }
  }, [content, editor]);

  return (
    <div className="note-editor-container">
      {/* Heading - No label, just the input */}
      <input
        type="text"
        value={heading}
        onChange={(e) => onHeadingChange?.(e.target.value)}
        placeholder="Note title..."
        disabled={disabled}
        maxLength={200}
        className="note-editor-heading"
      />

      {/* Summary - No label, placeholder explains it */}
      <div className="note-editor-section">
        <textarea
          ref={summaryRef}
          value={summary}
          onChange={(e) => {
            onSummaryChange?.(e.target.value);
            // Auto-resize
            e.target.style.height = 'auto';
            e.target.style.height = `${e.target.scrollHeight}px`;
          }}
          placeholder="Brief summary of this note..."
          disabled={disabled}
          rows={1}
          maxLength={500}
          className="note-editor-summary"
        />
        {summary.length > 400 && (
          <div className="note-editor-helper-text">
            {summary.length}/500 characters
          </div>
        )}
      </div>

      {/* Tags - No label, placeholder explains it */}
      <div className="note-editor-section">
        <TagInput
          tags={tags}
          onChange={onTagsChange}
          suggestions={tagSuggestions}
          placeholder="Add tags..."
        />
      </div>

      {/* TipTap Editor with label */}
      <div className="note-editor-section">
        <label className="note-editor-label">Content</label>
        <div className="note-editor-content-wrapper">
          <TipTapToolbar editor={editor} />
          <div className="note-editor-content-area">
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteEditor;
